/**
 * CypAdhdPreAppointmentDashboard — pre-assessment information for a CYP ADHD
 * patient.
 *
 * Two equal-width cards sit side by side:
 *   - Parent/carer forms: SNAP-IV 26 scores, then the guardian's history
 *     questionnaires (the primary source — ten forms).
 *   - School forms: the same two sections for the nominated school
 *     representative (four forms). School input is advisory, so the card
 *     stays visible with honest empty states when no one has been nominated.
 *
 * Each card uses the same order — Rating scales, then History — so a
 * clinician scanning left-to-right is comparing like with like, not hunting
 * for the SNAP scores in a third full-width panel below.
 *
 * The child answers nothing themselves, so there is no patient panel.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { DocumentTextIcon, ExclamationIcon } from '@heroicons/react/outline';
import InformantHistoryPanel from '../components/InformantHistoryPanel';
import InformantFeedbackButton from '../components/InformantFeedbackButton';
import ProviderManageInformantsForm from '../components/ProviderManageInformantsForm';
import FormDetailsPanel from '../components/FormDetailsPanel';
import {
  CypSnapScaleColumn,
  snapResultsFromInformantView,
} from './CypSnapScalesPanel';
import { findInformantAnswersChangedAfterGeneration } from '../../../../utils/formLockingUtils';

/** Resolve the first informant id in the map holding the given CYP role. */
function findInformantIdByRole(informantsMap = {}, role) {
  const entry = Object.entries(informantsMap).find(
    ([, mapEntry]) => mapEntry?.informantRole === role,
  );
  return entry ? entry[0] : null;
}

function FormsCard({
  title,
  informantName,
  informantRelationship,
  scaleResults,
  scaleEmptyText,
  onViewScaleDetails,
  historyContent,
  answersChangedWarning,
  headerAction,
  // When set, the card shows one short empty state instead of the rating
  // scales and history sections. Used when nobody has been nominated yet,
  // so the clinician is not shown two "nothing here" blocks plus a paragraph.
  emptyState,
}) {
  return (
    <div className="w-full p-5 bg-white rounded-xl shadow border border-gray-200 text-left">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-baseline gap-2">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {informantName ? (
            <span className="text-sm text-gray-500">
              {informantName}
              {informantRelationship ? ` (${informantRelationship})` : ''}
            </span>
          ) : null}
        </div>
        {headerAction}
      </div>

      {/* Answers-changed-after-generation warning — shown when the informant
          has updated any form after the CI or SSI report was generated. This
          alerts the clinician that the drafted report paragraphs may not
          reflect the latest answers. */}
      {answersChangedWarning && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <ExclamationIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" aria-hidden="true" />
          <p>{answersChangedWarning}</p>
        </div>
      )}

      {emptyState ? (
        // Same shape as Adult ADHD's "No scales available" empty state:
        // an icon, a short heading, and one sentence.
        <div className="text-center py-6 border rounded-lg">
          <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{emptyState.title}</h3>
          <p className="mt-1 text-sm text-gray-500">{emptyState.message}</p>
        </div>
      ) : (
        <>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Rating scales</h3>
          <p className="text-sm text-gray-600 mb-3">
            ADHD symptoms scale (SNAP-IV 26), rated over the past 6 months.
          </p>
          <CypSnapScaleColumn
            results={scaleResults}
            emptyText={scaleEmptyText}
            onViewDetails={onViewScaleDetails}
          />

          <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-3">History</h3>
          {historyContent}
        </>
      )}
    </div>
  );
}

/** SNAP-IV form key used by CYP ADHD informants (both parent/carer and school). */
const SNAP_IV_FORM_KEY = 'snapIV26Informant';

export default function CypAdhdPreAppointmentDashboard({
  patientDataModel,
  informantData = {},
  reportContent = {},
  patientId,
  refreshPatientData,
}) {
  const [isManageInformantsOpen, setIsManageInformantsOpen] = useState(false);

  // FormDetailsPanel state — shared by both parent/carer and school magnifying
  // glass icons so we only render one panel instance.
  const [scaleDetailForm, setScaleDetailForm] = useState(null);
  const [isScaleDetailOpen, setIsScaleDetailOpen] = useState(false);

  const informantsMap = patientDataModel?.informants || {};
  const guardianId = useMemo(
    () => findInformantIdByRole(informantsMap, 'legalGuardian'),
    [informantsMap],
  );
  const schoolRepId = useMemo(
    () => findInformantIdByRole(informantsMap, 'schoolRep'),
    [informantsMap],
  );

  const guardianView = guardianId ? informantData[guardianId] : null;
  const schoolRepView = schoolRepId ? informantData[schoolRepId] : null;

  const guardianMeta = guardianId ? informantsMap[guardianId] : null;
  const schoolRepMeta = schoolRepId ? informantsMap[schoolRepId] : null;

  const schoolRelationship = schoolRepMeta?.schoolName
    ? `${schoolRepMeta?.informantRelationshipToPatient || 'School representative'} — ${schoolRepMeta.schoolName}`
    : schoolRepMeta?.informantRelationshipToPatient;

  // The guardian's profile holds schoolRepNomination data — when the
  // parent/carer explicitly skipped nominating a school rep, we surface
  // that reason to clinicians instead of the generic "not yet nominated"
  // message. This mirrors how Adult ADHD shows informant skip reasons.
  const schoolRepNomination = guardianView?.schoolRepNomination;
  const schoolNominationSkipped =
    !schoolRepId && schoolRepNomination?.status === 'skipped';

  // Open the FormDetailsPanel for a given informant's SNAP-IV form.
  // Only wired when the informant has completed results — a magnifying glass
  // on an empty/unscored row would open an empty panel.
  const openScaleDetail = useCallback((informantId) => {
    setScaleDetailForm({
      key: SNAP_IV_FORM_KEY,
      source: 'informant',
      informantId,
    });
    setIsScaleDetailOpen(true);
  }, []);

  const handleViewGuardianScale = guardianId && snapResultsFromInformantView(guardianView)
    ? () => openScaleDetail(guardianId)
    : undefined;
  const handleViewSchoolScale = schoolRepId && snapResultsFromInformantView(schoolRepView)
    ? () => openScaleDetail(schoolRepId)
    : undefined;

  // Answers-changed-after-generation: compare each informant's formInstance
  // updatedAt timestamps against the CI/SSI reportGeneratedAt stamps. The
  // result is per-informant, keyed by uid, with `ci` and/or `ssi` entries.
  const answersChanged = useMemo(() => {
    // Build a lookup of informant form instances from the enriched data.
    const ifi = {};
    for (const [id, view] of Object.entries(informantData)) {
      if (view?.formInstances) ifi[id] = view.formInstances;
    }
    return findInformantAnswersChangedAfterGeneration({
      informantFormInstances: ifi,
      reportContent,
    });
  }, [informantData, reportContent]);

  // Build per-card warning strings when the informant's answers were updated
  // after a CI or SSI report was generated.
  const guardianWarning = useMemo(() => {
    if (!guardianId || !answersChanged[guardianId]) return null;
    const entry = answersChanged[guardianId];
    const stages = [entry.ci && 'Clinical Interview', entry.ssi && 'Semi-Structured Interview'].filter(Boolean);
    return `This respondent's answers were updated after the ${stages.join(' and ')} report was generated. The drafted paragraphs may not reflect the latest answers.`;
  }, [guardianId, answersChanged]);

  const schoolRepWarning = useMemo(() => {
    if (!schoolRepId || !answersChanged[schoolRepId]) return null;
    const entry = answersChanged[schoolRepId];
    const stages = [entry.ci && 'Clinical Interview', entry.ssi && 'Semi-Structured Interview'].filter(Boolean);
    return `This respondent's answers were updated after the ${stages.join(' and ')} report was generated. The drafted paragraphs may not reflect the latest answers.`;
  }, [schoolRepId, answersChanged]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col lg:flex-row justify-between items-stretch gap-3 lg:gap-5">
        <div className="w-full lg:w-1/2">
          <FormsCard
            title="Parent/carer forms"
            informantName={guardianMeta?.informantName || guardianView?.displayName}
            informantRelationship={guardianMeta?.informantRelationshipToPatient}
            scaleResults={snapResultsFromInformantView(guardianView)}
            scaleEmptyText="Not completed yet"
            onViewScaleDetails={handleViewGuardianScale}
            answersChangedWarning={guardianWarning}
            headerAction={guardianId ? (
              <InformantFeedbackButton
                informantViewData={guardianView}
                informantId={guardianId}
                contactNoun="parent/carer"
              />
            ) : undefined}
            historyContent={
              guardianId ? (
                <InformantHistoryPanel
                  key={guardianId}
                  panelTitle="Parent/carer forms"
                  informantId={guardianId}
                  informantViewData={guardianView}
                  patientData={patientDataModel}
                  informantDisplayName={guardianMeta?.informantName || guardianView?.displayName}
                  informantRelationship={guardianMeta?.informantRelationshipToPatient}
                  contactNoun="parent/carer"
                  embedded
                />
              ) : (
                <p className="text-sm text-gray-600">
                  No parent/carer account exists for this patient yet. Add one from
                  Manage parents and school.
                </p>
              )
            }
          />
        </div>

        <div className="w-full lg:w-1/2">
          <FormsCard
            title="School forms"
            informantName={schoolRepMeta?.informantName || schoolRepView?.displayName}
            informantRelationship={schoolRelationship}
            scaleResults={snapResultsFromInformantView(schoolRepView)}
            scaleEmptyText="School input not received"
            onViewScaleDetails={handleViewSchoolScale}
            answersChangedWarning={schoolRepWarning}
            headerAction={schoolRepId ? (
              <InformantFeedbackButton
                informantViewData={schoolRepView}
                informantId={schoolRepId}
                contactNoun="school representative"
              />
            ) : undefined}
            emptyState={!schoolRepId && !schoolNominationSkipped ? {
              title: 'No school forms available',
              message: 'No school representative has been nominated yet.',
            } : null}
            historyContent={
              schoolRepId ? (
                <InformantHistoryPanel
                  key={schoolRepId}
                  panelTitle="School forms"
                  informantId={schoolRepId}
                  informantViewData={schoolRepView}
                  patientData={patientDataModel}
                  informantDisplayName={schoolRepMeta?.informantName || schoolRepView?.displayName}
                  informantRelationship={schoolRelationship}
                  contactNoun="school representative"
                  embedded
                />
              ) : schoolNominationSkipped ? (
                <div>
                  {/* Amber skip card — matches the Adult ADHD/Autism pattern
                      used in InformantManagementContent for skipped informants. */}
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
                    <p className="text-sm font-semibold text-amber-800">
                      School representative — Skipped
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      <span className="font-medium">Reason given:</span>{' '}
                      <em>&ldquo;{schoolRepNomination.reason}&rdquo;</em>
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    The parent/carer skipped nominating a school representative.
                    You can still add one from Manage parents and school — school
                    input is advisory and the assessment can proceed without it.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsManageInformantsOpen(true)}
                    className="mt-3 text-sm font-medium text-violet-700 underline hover:text-violet-900"
                  >
                    Open Manage parents and school
                  </button>
                </div>
              ) : (
                // Not nominated and not skipped. FormsCard replaces the
                // rating-scales and history sections with emptyState, so
                // there is nothing to render here.
                null
              )
            }
          />
        </div>
      </div>

      <ProviderManageInformantsForm
        isOpen={isManageInformantsOpen}
        onClose={() => setIsManageInformantsOpen(false)}
        patientDataModel={patientDataModel}
        refreshPatientData={refreshPatientData}
        patientId={patientId}
        reportContent={reportContent}
      />

      {/* Shared FormDetailsPanel for SNAP-IV raw questions/answers. Renders
          the same slide-over panel that InformantHistoryPanel and Adult ADHD's
          PatientScalesPanel use for the magnifying glass drill-down. */}
      <FormDetailsPanel
        isOpen={isScaleDetailOpen && !!scaleDetailForm}
        formInfo={scaleDetailForm}
        patientData={patientDataModel}
        informantData={informantData}
        onClose={() => setIsScaleDetailOpen(false)}
      />
    </div>
  );
}
